import { createReducer, on } from '@ngrx/store';
import { AuthActions, RouterActions, AppActions, BusinessActions, UsersActions } from '@actions/index'
import { UserModel, ResellerModel } from '@models/index'
import { emptyResellerModel } from '@utilities/data-utils';
import { LocationBusinessMetadata } from '@actions/auth/auth.actions';
import { Role } from '@enums/role.enum';

/**
 * Auth's app State. Defines the
 * app State that will store auth's
 * information.
 */
export interface State {
  /**
   * loggedUser: An object that contains
   * app current logged user. Refer to
   * {@link UserModel}
   */
  loggedUser: UserModel;
  /**
   * isExecutingTask: A boolean that
   * specifies if an app petition
   * is waiting for a response
   */
  isExecutingTask: boolean;
  /**
   * changePsw: Defines wheter the
   * password was changed successfully
   * or not
   */
  changePsw: boolean;
  /**
   * error: Stores the auth errors
   */
  error: string;
  /**
   * selectedReseller: An object that stores
   * current selected reseller depending on
   * the logged user.
   */
  selectedReseller: ResellerModel;
  /**
   * locationMetadata: used by the search box
   * to quickly find locations that can be
   * navigated/selected. Null indicates loading.
   */
  locationMetadata: { businessId: string, businessName: string, locations: LocationBusinessMetadata[] }[];
}

/**
 * initialState stores the initial values
 * for Auth's app state
 */
const initialState: State = {
  loggedUser: {
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    role: {
      id: '',
      name: Role.None,
      shortName: '',
      labelName: '',
      createdAt: null,
      updatedAt: null,
      deletedAt: null,
      deleted: false,
    },
    permissions: []
  },
  isExecutingTask: false,
  changePsw: false,
  error: "",
  selectedReseller: {
    id: "",
    name: "",
    shortName: "",
    logo: "",
    siteURL: "",
    favIcon: ""
  },
  locationMetadata: null
};

/**
 * reducer creates the auth's reducer.
 * This reducer listens to 
 * auth actions to store the returned 
 * data to auth's app state depending
 * on the action
 */
export const reducer = createReducer(
  initialState,
  // Even thought the `state` is unused, it helps infer the return type
  on(AuthActions.loginSuccess, (state, { user }) => ({ ...state, loggedUser: user })),
  on(AuthActions.logOutSuccess, (state) => ({
    ...state,
    loggedUser: initialState.loggedUser,
    isExecutingTask: initialState.isExecutingTask,
    changePsw: initialState.changePsw
  })),
  on(AuthActions.changePasswordSuccess, (state) => ({ ...state, changePsw: true })),
  on(AuthActions.setPswChanged, (state, { pswChanged }) => ({ ...state, changePsw: pswChanged })),
  on(AuthActions.login, state => ({ ...state, isExecutingTask: true })),
  on(AuthActions.loginFailure, (state, { error }) => ({ ...state, isExecutingTask: false, error: error })),
  on(RouterActions.navigateAuth, () => ({ ...initialState })),
  on(AppActions.clearError, state => ({ ...state, isExecutingTask: false, error: '' })),
  on(AuthActions.selectReseller, (state, { payload }) => ({ ...state, selectedReseller: payload.id ? payload : initialState.selectedReseller })),
  on(BusinessActions.selectBusinessProfile, (state, { business: selectedBusinessProfile }) => {
    let userData = null;
    if (state.loggedUser.businesses) {
      userData = state.loggedUser.businesses.find(item => item.business.id == selectedBusinessProfile.id)
    }

    if (userData) {
      return {
        ...state,
        loggedUser: {
          ...state.loggedUser,
          permissions: userData.permissions,
          role: userData.role
        }
      }
    }
    return {
      ...state,
    }
  }),
  on(AuthActions.getResellerSuccess, (state, reseller) => ({ ...state, selectedReseller: reseller })),
  on(AuthActions.getReseller, (state) => ({ ...state, selectedReseller: initialState.selectedReseller })),
  on(AuthActions.searchLocationAutocompleteOptions, (state, _) => ({
    ...state,
    locationMetadata: null
  })),
  on(AuthActions.searchLocationAutocompleteOptionsSuccess, (state, { locationMetadata }) => ({
    ...state,
    locationMetadata
  })),
  on(UsersActions.saveUserPhoneNumberSuccess, (state, { user }) => ({
    ...state,
    loggedUser: {
      ...state.loggedUser,
      phoneNumber: user.phoneNumber,
      isEnableReviewText: user.isEnableReviewText,
      isShowPhoneNumberUpdate: user.isShowPhoneNumberUpdate,
    }
  })),
  on(UsersActions.updateShowModalPhoneNumberSuccess, (state, { user }) => ({
    ...state,
    loggedUser: {
      ...state.loggedUser,
      isShowPhoneNumberUpdate: user.isShowPhoneNumberUpdate,
      isEnableReviewText: user.isEnableReviewText,
      phoneNumber: user.phoneNumber,
    }
  })),
);

/**
 * Returns the loggedUser property
 * from auth's app state
 * @param state 
 */
export const getLoggedUser = (state: State) => state.loggedUser;
/**
 * Returns the logged user role 
 * from auth's app state
 * @param state 
 */
export const getRoleLoggedUser = (state: State) => state.loggedUser.role.name;
/**
 * Returns the isExecutingTask property
 * from auth's app state
 * @param state 
 */
export const getIsExecutedTask = (state: State) => state.isExecutingTask;
/**
 * Returns the changePsw property
 * from auth's app state
 * @param state 
 */
export const getPswChanged = (state: State) => state.changePsw;
/**
 * Returns the error property
 * from auth's app state
 * @param state 
 */
export const getError = (state: State) => state.error;
/**
 * Returns the selectedReseller property
 * from auth's app state
 * @param state 
 */
export const getSelectedReseller = (state: State) => state.selectedReseller;
/**
 * Returns the location/business metadata for selection by the search box
 */
export const getLocationMetadata = (state: State) => state.locationMetadata;
